import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createAdmin, deleteAdmin, getAdmins } from '../../api/dashboard';
import PageHeader from '../../components/PageHeader';
import StatusMessage from '../../components/StatusMessage';
import { adminErrorMessage } from './adminErrors';
import { ADMIN_CREATION_ENABLED } from './features';
import './Admin.css';

export const MAX_ADMIN_ACCOUNTS = 10;

function CreateAdminRow({ onCancel, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const fields = { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() };
    if (!fields.firstName && !fields.lastName && !fields.email) { onCancel(); return; }
    if (!fields.firstName) { setError('First name is required.'); return; }
    if (!fields.lastName) { setError('Last name is required.'); return; }
    if (!fields.email) { setError('Email is required.'); return; }
    setSaving(true);
    setError('');
    try {
      onCreated(await createAdmin(fields));
    } catch (err) {
      setError(adminErrorMessage(err, 'Could not create the admin account. Please try again.'));
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="list-card list-card-creating">
      <div className="list-card-main">
        <div className="create-fields">
          <input ref={inputRef} className="create-input" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name" aria-label="First name" disabled={saving} onKeyDown={onKeyDown} />
          <input className="create-input" value={lastName} onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name" aria-label="Last name" disabled={saving} onKeyDown={onKeyDown} />
          <input type="email" className="create-input" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" aria-label="Email" disabled={saving} onKeyDown={onKeyDown} />
        </div>
        {error && <span className="field-error" role="alert">{error}</span>}
        <p className="admin-hint">
          New accounts get a temporary password by email. An existing Aren account in your organization is given admin access instead.
        </p>
      </div>
      <div className="list-card-actions">
        <button type="button" className="title-button" onClick={commit} disabled={saving}>
          {saving ? 'Creating…' : 'Create'}
        </button>
        <button type="button" className="text-button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function AdminRow({ admin, isLastAdmin, onDeleted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteAdmin(admin.id);
      onDeleted(admin.id);
    } catch (err) {
      setError(adminErrorMessage(err, 'Could not remove this admin. Please try again.'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const disabledReason = admin.isSelf
    ? "You can't remove your own admin access."
    : isLastAdmin ? 'At least one admin must remain.' : undefined;

  return (
    <div className="list-card">
      <div className="list-card-main">
        <div className="list-card-name-row">
          <span className="list-card-name">{admin.name || admin.email}</span>
          {admin.isSelf && <span className="count-chip">You</span>}
        </div>
        <span className="list-card-meta">{admin.email}</span>
        {error && <span className="field-error" role="alert">{error}</span>}
      </div>
      <div className="list-card-actions">
        {confirmDelete ? (
          <div className="delete-confirm">
            <span className="delete-confirm-text">Remove admin access?</span>
            <button type="button" className="delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </button>
            <button type="button" className="text-button" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
          </div>
        ) : (
          <button
            type="button"
            className="remove-button"
            onClick={() => setConfirmDelete(true)}
            disabled={Boolean(disabledReason)}
            title={disabledReason || 'Remove admin access'}
            aria-label={`Remove admin access for ${admin.name || admin.email}`}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

const Admins = () => {
  const [admins, setAdmins] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAdmins((await getAdmins()).admins || []);
    } catch (err) {
      setError(adminErrorMessage(err, "We couldn't load the admins."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const atCapacity = (admins?.length || 0) >= MAX_ADMIN_ACCOUNTS;

  return (
    <>
      <PageHeader
        title="Admins"
        actions={(
          <div className="title-actions">
            {admins && <span className="count-pill">{admins.length}/{MAX_ADMIN_ACCOUNTS} accounts</span>}
            <button
              type="button"
              className="reload-button"
              onClick={() => setCreating(true)}
              disabled={!ADMIN_CREATION_ENABLED || !admins || creating || atCapacity}
              title={!ADMIN_CREATION_ENABLED ? "Adding admins isn't available yet."
                : atCapacity ? `Maximum of ${MAX_ADMIN_ACCOUNTS} admin accounts reached` : undefined}
            >
              + Add Admin
            </button>
          </div>
        )}
      />
      <div className="component-container">
        <div className="admin-content">
          {error && <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>}
          {!error && !admins && <StatusMessage kind="loading">Loading admins…</StatusMessage>}
          {admins && (
            <>
              {atCapacity && (
                <p className="admin-hint">
                  Maximum of {MAX_ADMIN_ACCOUNTS} admin accounts reached. Remove one to add another.
                </p>
              )}
              <div className="card-list">
                {ADMIN_CREATION_ENABLED && creating && !atCapacity && (
                  <CreateAdminRow
                    onCancel={() => setCreating(false)}
                    onCreated={(admin) => { setAdmins((prev) => [...prev, admin]); setCreating(false); }}
                  />
                )}
                {admins.length === 0 && !creating && (
                  <p className="admin-hint">No admin accounts yet. Click "+ Add Admin" to create one.</p>
                )}
                {admins.map((admin) => (
                  <AdminRow
                    key={admin.id}
                    admin={admin}
                    isLastAdmin={admins.length <= 1}
                    onDeleted={(id) => setAdmins((prev) => prev.filter((a) => a.id !== id))}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Admins;
